import { EmployeeStates } from "../pages/settings/EmployeeDatasourceSettings";
import { DesignatedPersonList } from "./designatedPerson";
import { StrikePriceType } from "./EsopPlan";
import {
  EventType,
  LiquidityState,
  MonetaryEventState,
  MonetaryOffers,
  MonetaryTransactionType,
  ParticipationConstraints,
  RecurringIntervalUnit,
  RecurringSetting,
} from "./OptionBuyback";

export enum RecurringIntervalUnitV2 {
  DAYS = "DAYS",
  MONTH = "MONTH",
  QUARTER = "QUARTER",
  YEAR = "YEAR",
}

export enum FmvPriceType {
  PREVIOUS_DAY_CLOSING = "PREVIOUS_DAY_CLOSING",
  EXERCISE_DAY_AVERAGE = "EXERCISE_DAY_AVERAGE",
}

export enum ExerciseESignMethod {
  NONE = "",
  DIGIO_AADHAAR = "digio_aadhar",
  DIGIO_ELECTRONIC = "digio_electronic",
}

export interface Financier {
  id: string;
  name: string;
  details: string;
}

export type ExercisePaymentRoundingStrategy =
  | "ROUND_UP"
  | "ROUND_DOWN"
  | "ROUND_OFF";

export type ExercisePaymentRoundingConfiguration = {
  enabled: boolean;
  roundingStrategy: ExercisePaymentRoundingStrategy;
  multiple: number;
};

export type MonetaryDetailsV2 = {
  sellingPrice: number;
  fmvPrice: number;
  price: number;
  miscCharges: number;
  percentageOfNetVestedOptions: number;
  participantListMethod: string;
  includeStrikePrice: boolean;
  generateExerciseLetters: boolean;
  transactionPaymentEnabled: boolean;
  taxPaymentEnabled?: boolean;
  hissaGeneratedPaymentEnabled: boolean;
  isCompanyPublic: boolean;
  fmvPriceType: FmvPriceType;
  isDematAccountMandatory: boolean;
  allowedDematDepositories: string[];
  isPaymentLinkAnImage: boolean;
  paymentImage?: UploadedFile;
  paymentLink?: string;
  exercisePaymentDocumentUploadMandatory?: boolean;
  paymentData?: {
    taxIdentifier?: string;
    paymentIdentifier?: string;
  };
  hissaGeneratedTaxPaymentEnabled: boolean;
  isTaxPaymentLinkAnImage: boolean;
  taxPaymentImage?: UploadedFile;
  taxPaymentLink?: string;
  taxPaymentDocumentUploadMandatory?: boolean;
  autoApproveExerciseRequests: boolean;
  sendTransactionEmails: boolean;
  sendTransactionEmailsToAdmin: boolean;
  sendTransactionEmailsToEmployee: boolean;
  employeeSignatureRequired: boolean;
  enableESignForExerciseLetter: boolean;
  eSignMethod: ExerciseESignMethod;
  customTemplates?: UploadedFile[];
  defaultTemplateLink?: string;
  generateOffers: boolean;
  defaultEmailTemplates?: {
    to: string;
    templateType: string;
    templateLink: string;
    sendEmail: boolean;
    templateName: string;
    description: string;
  }[];
  optionWillingnessMultiplier?: number;
  isFileUploaded: boolean;
  isLatestFMV: boolean;
  considerBlackOutDays?: boolean;
  blackOutDays?: string[];
  taxMandatoryForCompletion?: boolean;
  selectionOfGrants?: string;
  strictlyConsiderCompanyCurrency?: boolean;
  taxDetailsInfoText?: string;
  additionalPaymentInfoText?: string;
  exercisePaymentInfoText?: string;
  exercisePaymentApprovalNoteText?: string;
  taxPaymentApprovalNoteText?: string;
  exerciseEsopFinancingEnabled?: boolean;
  exerciseFinanciers?: Financier[];
  taxEsopFinancingEnabled?: boolean;
  taxFinanciers?: Financier[];
  // Workflow config (v2)
  workflowSteps?: WorkflowStepConfig[];
  workflowVersion?: 1 | 2;
  allowMultipleRequests?: boolean;
  exerciseRounding?: ExercisePaymentRoundingConfiguration;
  taxRounding?: ExercisePaymentRoundingConfiguration;
};

// ── Workflow types (mirrored from backend types/exerciseWorkflow.ts) ────────

export enum WorkflowStepType {
  WILLINGNESS = "WILLINGNESS",
  ADMIN_APPROVAL = "ADMIN_APPROVAL",
  DOCUMENT_SIGNING = "DOCUMENT_SIGNING",
  EXERCISE_PAYMENT = "EXERCISE_PAYMENT",
  TAX_PAYMENT = "TAX_PAYMENT",
  SHARE_ALLOCATION = "SHARE_ALLOCATION",
}

export enum WorkflowRequirementKind {
  FIELD = "FIELD",
  DOCUMENT = "DOCUMENT",
  DECLARATION = "DECLARATION",
  QUESTION = "QUESTION",
}

export enum WorkflowRequirementFilter {
  ALL = "ALL",
}

export enum WorkflowFieldInputType {
  TEXT = "TEXT",
  NUMBER = "NUMBER",
  DATE = "DATE",
}

export enum WorkflowPaymentType {
  HISSA_GENERATED = "HISSA_GENERATED",
  EXTERNAL = "EXTERNAL",
}

export type WorkflowStepDetailConfig = {
  requirements?: WorkflowRequirement[];
  signatureRequired?: boolean;
  eSignEnabled?: boolean;
  eSignMethod?: ExerciseESignMethod;
  autoCompleteAfterSigning?: boolean;
  paymentEnabled?: boolean;
  paymentType?: WorkflowPaymentType;
  paymentLink?: string;
  paymentIdentifier?: string;
  documentUploadMandatory?: boolean;
  taxPaymentEnabled?: boolean;
  taxPaymentType?: WorkflowPaymentType;
  taxPaymentLink?: string;
  taxMandatoryForCompletion?: boolean;
  taxIdentifier?: string;
};

export type WorkflowRequirement = {
  id: string;
  kind: WorkflowRequirementKind;
  order: number;
  label?: string;
  description?: string;
  required: boolean;
  field?: {
    inputType: WorkflowFieldInputType;
    maxLength?: number;
  };
  document?: {
    acceptedTypes?: string[];
    maxSizeMB?: number;
    maxFiles?: number;
  };
  declaration?: { text: string };
  question?: { options: { id: string; label: string }[] };
};

export type WorkflowStepInformation = {
  configSnapshot?: WorkflowRequirement[];
  fields: Record<string, string>;
  choices: Record<string, string>;
  declarations: Record<
    string,
    { accepted: boolean; text: string; acceptedAt?: string }
  >;
  documents: {
    id: string;
    name: string;
    requirementId: string;
    url: string;
  }[];
};

export type WorkflowStepConfig = {
  type: WorkflowStepType;
  order: number;
  enabled: boolean;
  label?: string;
  config?: WorkflowStepDetailConfig;
};

export const WORKFLOW_STEP_DEFAULT_LABELS: Record<WorkflowStepType, string> = {
  [WorkflowStepType.WILLINGNESS]: "Employee Gives Willingness",
  [WorkflowStepType.ADMIN_APPROVAL]: "Admin Approval",
  [WorkflowStepType.DOCUMENT_SIGNING]: "Document Creation & Signing",
  [WorkflowStepType.EXERCISE_PAYMENT]: "Exercise Payment",
  [WorkflowStepType.TAX_PAYMENT]: "Tax Payment",
  [WorkflowStepType.SHARE_ALLOCATION]: "Track Until Shares Allocated",
};

export const CONFIGURABLE_STEP_TYPES: WorkflowStepType[] = [
  WorkflowStepType.DOCUMENT_SIGNING,
  WorkflowStepType.EXERCISE_PAYMENT,
  WorkflowStepType.TAX_PAYMENT,
];

export type BlackOutDayFilter = {
  departments: string[];
  designations: string[];
  employeeIds: string[];
};

export type BlackOutDay = {
  id?: string;
  startDate: string | Date;
  endDate: string | Date;
  reason: string;
  filters: BlackOutDayFilter;
  companyId?: string;
  eventId?: string;
  createdBy?: string;
  updatedBy?: string;
  designatedPerson?: DesignatedPersonList;
};

export type BlackOutDayResponse = {
  id: string;
  startDate: Date | null;
  endDate: Date | null;
  reason: string;
  employeeId: string;
  exists: boolean;
};

export interface UploadedFile {
  name: string;
  type: string;
  size: number;
  base64: string;
  id?: string;
  canEmployeeView?: boolean;
  s3Url?: string;
  file?: Buffer;
  isUserUploaded?: boolean;
  tags?: string[];
  entityType?: string;
}

export interface AllocateSharesReq {
  employeeId: string;
  hrId: string;
  requestId: string;
  transactionId: string;
  exerciseDate: Date;
  allocationDate: Date;
  securityClassId?: string;
}

export interface TransactionDetailsData {
  id?: string;
  employeeInfo?: any;
  companyId: string;
  companyName: string;
  description: string;
  startDate: string;
  endDate?: string;
  vestingCutOffDate?: string;
  monetaryDetails: MonetaryDetailsV2;
  eventState: MonetaryEventState;
  liquidityState: LiquidityState;
  eventName: string;
  offerFilter: OfferFilterV2;
  isOnAutoPilot: boolean;
  askWillingness: boolean;
  recurringSetting: RecurringSetting;
  eventPercentage: number;
  eventType: EventType;
  transactionType: MonetaryTransactionType;
  buyerName: any;
  isDeleted: boolean;
  monetaryOffers: any[];
  file?: string;
  selectedEmployeesList?: EmployeeOfferDetails[];
  previewList?: EmployeeOfferDetails[];
  previewListDataWithFilters?: EmployeeOfferDetails[];
  notes?: string;
  minWillingnessPercentage?: number;
  startTransaction?: boolean;
}

export type EmployeeOfferDetails = {
  id?: string;
  employeeId: string;
  employeeName: string;
  companyName: string;
  hrId: string;
  employeeDepartment: string;
  employeeJoiningDate: Date;
  employmentType: EmploymentType;
  employeeState: string;
  employeeDesignation: string;
  subsidiaryCompanyId: string;
  subsidiaryCompanyName: string;
  tenure: number;
  grantRelatedDetails: GrantOfferDetails[];
  participatedEventDates: Date[];
  isManuallyUploaded: boolean;
  nationality?: string;
  companyId: string;
  minWillingness?: number;
  minWillingnessPercentage?: number;
  offerState?: string;
  generatedBy?: OfferGeneratedByType;
  totalGrantedOptions: number;
  totalVestedOptions: number;
  totalEligibleOptions: number;
  totalCashExercisedOptions: number;
  totalStockExercisedOptions: number;
  totalExercisedOptions: number;
  totalNetVestedOptions?: number;
  totalWillingness: number;
  transactionCompensationDetails?: TransactionCompensationDetails;
  paymentDetails?: PaymentDetails[];
  exerciseDate?: Date;
  requestId?: string;
  transactionId?: string;
  monetaryTransactionDetails?: any;
};

export type PaymentDetails = {
  id?: string;
  updatedAt?: Date | string;
  companyId: string;
  referenceId: string;
  employeeId: string;
  requestId: string;
  eventId: string;
  eventType: string;
  paymentStatus: string;
  amount: number;
  currency: string;
  paymentDate: Date;
  retryCount: number;
  paymentType: string;
  documentLinks?: UploadedFile[];
  metaData?: {
    financingType?: "SELF" | "ESOP_FINANCING";
    financierId?: string;
    financierName?: string;
    remarks?: string;
    modeOfPayment?: string;
    [key: string]: any;
  };
};

export type TransactionCompensationDetails = {
  salary?: number;
  isNewRegime?: boolean;
  taxDetails?: {
    taxPercentage: number;
    cessPercentage: number;
    surchargePercentage: number;
  };
  perquisiteAmount?: number;
  perquisiteTaxAmount?: number;
  cessAmount?: number;
  surchargeAmount?: number;
  calculatedTaxPercentage?: number;
  calculatedCessChargePercentage?: number;
  calculatedSurchargePercentage?: number;
  totalStrikePrice?: number;
  totalTaxAmount?: number;
  totalAmount?: number;
};

export enum OfferGeneratedByType {
  SYSTEM = "SYSTEM",
  FILE = "FILE",
  ADMIN = "ADMIN",
  EMPLOYEE = "EMPLOYEE",
}

export type EmployeeDetailsUploaded = {
  employeeId?: string;
  hrId?: string;
  overridenEligibility?: number;
  overridenMinWillingness?: number;
};

export type GrantOfferDetails = {
  identifier: string;
  id: string;
  grantedOptions: number;
  vestedOptions: number;
  eligibleOptions: number;
  cashExercisedOptions: number;
  stockExercisedOptions: number;
  cashExercisedOptionsRequested: number;
  stockExercisedOptionsRequested: number;
  netVestedOptions?: number;
  strikePrice: number;
  strikePriceType: StrikePriceType;
  conversionRatio: number;
  conversionMethodology: string;
  planId: string;
  planName: string;
  planType: string;
  willingness?: number;
  monetaryOfferId?: string;
  fmvPrice?: number;
};

export type NumberType = {
  min: number;
  max: number;
};

export type NameAndId = {
  name: string;
  id: string;
  hrId?: string;
  employeeId?: string;
};

export enum EmploymentStatus {
  EMPLOYED = "EMPLOYED",
  RESIGNED = "RESIGNED",
  RETIRED = "RETIRED",
  PENDING = "PENDING",
  TERMINATED = "TERMINATED",
}

export enum EmploymentType {
  EMPLOYEE = "EMPLOYEE",
  CONSULTANT = "CONSULTANT",
}

export type OfferFilterV2 = {
  department?: string[];
  tenure?: NumberType;
  vestedOptions?: NumberType;
  eligibleOptions?: NumberType;
  willingness?: NumberType;
  willingnessPercentage?: NumberType;
  participationConstraints?: ParticipationConstraints[];
  planIds?: NameAndId[];
  employmentType?: EmploymentType[];
  status?: string[];
  companyIds?: NameAndId[];
  employeeIds?: NameAndId[];
  excludeEmployeeIds?: NameAndId[];
  selectedEmployeeIds?: NameAndId[];
  nationality?: string[];
};

export const defaultTransactionDetailsData: TransactionDetailsData = {
  id: "",
  companyId: "",
  description: "",
  startDate: "",
  endDate: "",
  notes: "",
  vestingCutOffDate: "",
  minWillingnessPercentage: 0,
  monetaryDetails: {
    percentageOfNetVestedOptions: 0,
    sellingPrice: 0,
    fmvPrice: 0,
    price: 10,
    miscCharges: 0,
    paymentLink: "",
    generateOffers: true,
    includeStrikePrice: true,
    generateExerciseLetters: false,
    transactionPaymentEnabled: false,
    taxPaymentEnabled: false,
    autoApproveExerciseRequests: false,
    sendTransactionEmails: false,
    employeeSignatureRequired: false,
    enableESignForExerciseLetter: false,
    hissaGeneratedPaymentEnabled: true,
    hissaGeneratedTaxPaymentEnabled: true,
    isPaymentLinkAnImage: false,
    isTaxPaymentLinkAnImage: false,
    sendTransactionEmailsToAdmin: false,
    sendTransactionEmailsToEmployee: false,
    isFileUploaded: false,
    participantListMethod: "Upload Excel",
    isLatestFMV: false,
    isCompanyPublic: false,
    isDematAccountMandatory: false,
    allowedDematDepositories: ["CDSL", "NSDL"],
    fmvPriceType: FmvPriceType.PREVIOUS_DAY_CLOSING,
    considerBlackOutDays: false,
    eSignMethod: ExerciseESignMethod.NONE,
    allowMultipleRequests: false,
    exerciseRounding: {
      enabled: false,
      roundingStrategy: "ROUND_OFF",
      multiple: 1,
    },
    taxRounding: {
      enabled: false,
      roundingStrategy: "ROUND_OFF",
      multiple: 1,
    },
  },
  eventName: "",
  offerFilter: {
    department: [],
    tenure: { min: 0, max: 0 },
    vestedOptions: { min: 0, max: 0 },
    eligibleOptions: { min: 0, max: 0 },
    willingness: { min: 0, max: 0 },
    willingnessPercentage: { min: 0, max: 0 },
    participationConstraints: [],
    planIds: [],
    employmentType: [],
    status: [],
    companyIds: [],
    employeeIds: [],
    excludeEmployeeIds: [],
    selectedEmployeeIds: [],
    nationality: [],
  },
  isOnAutoPilot: false,
  askWillingness: false,
  recurringSetting: {
    isRecurring: false,
    recurringInterval: 1,
    recurringIntervalUnit: RecurringIntervalUnit.MONTH,
  },
  transactionType: MonetaryTransactionType.EXERCISE,
  companyName: "",
  eventState: MonetaryEventState.DRAFT,
  liquidityState: LiquidityState.DRAFT,
  eventPercentage: 0,
  eventType: EventType.CASH,
  buyerName: undefined,
  isDeleted: false,
  monetaryOffers: [],
};
